import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { Bank, Warning, CheckCircle, X, ArrowsClockwise, Globe, Link as LinkIcon } from "@phosphor-icons/react";

/**
 * VietQRPanel — lets the card owner attach a Vietnamese bank account so the
 * QR on the card flips from the Zcardon tracking URL to an EMVCo VietQR
 * payload scannable by Vietnamese banking apps (Vietcombank, BIDV, MB Bank,
 * Techcombank, VietinBank, ACB, etc.) via the NAPAS network.
 *
 * Important UX: this is irreversible per-scan — once a Vietnamese banking
 * app scans the card and the receiver authorizes the transfer, VND moves
 * from the payer's bank to the configured Vietnamese account, NOT to the
 * Zcardon USD balance. We surface a loud yellow warning before commit.
 */
export default function VietQRPanel({ card, onChange }) {
  const t = useT();
  const [banks, setBanks] = useState([]);
  const [config, setConfig] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // form state
  const [bankBin, setBankBin] = useState("970436"); // Default Vietcombank
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState((card?.receiver_name || "").toUpperCase());
  const [city, setCity] = useState("HA NOI");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get(`/cards/${card.id}/vietqr`);
      setConfig(r.data.enabled ? r.data.config : null);
      // If enabled, also fetch analytics so we can show "live X days · N views · M payments"
      if (r.data.enabled) {
        try {
          const a = await api.get(`/cards/${card.id}/vietqr/analytics`);
          setAnalytics(a.data.analytics);
        } catch {
          setAnalytics(null);
        }
      } else {
        setAnalytics(null);
      }
    } catch (err) {
      // 404 means not enabled yet — that's fine
      setConfig(null);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [card.id]);

  useEffect(() => {
    api.get("/public/vietqr/banks").then((r) => setBanks(r.data.banks || [])).catch(() => setBanks([]));
    loadConfig();
  }, [loadConfig]);

  const handleAttach = async () => {
    if (!accountNumber.trim() || !accountName.trim()) {
      toast.error(t("vietqr.toast.required"));
      return;
    }
    if (!confirmed) {
      toast.error(t("vietqr.toast.confirm_first"));
      return;
    }
    setBusy(true);
    try {
      const body = {
        bank_bin: bankBin,
        account_number: accountNumber.replace(/\D/g, ""),
        account_name: accountName.toUpperCase().slice(0, 25),
        city: city.toUpperCase().slice(0, 15),
        service_type: "account",
      };
      if (amount && Number(amount) > 0) body.amount = Number(amount);
      if (reference.trim()) body.reference = reference.trim().slice(0, 25);
      const r = await api.post(`/cards/${card.id}/vietqr`, body);
      toast.success(r.data.message || t("vietqr.toast.attached_default"));
      await loadConfig();
      setShowForm(false);
      setConfirmed(false);
      if (onChange) onChange();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("vietqr.toast.attach_failed"));
    } finally {
      setBusy(false);
    }
  };

  const handleDetach = async () => {
    if (!confirm(t("vietqr.toast.detach_confirm"))) return;
    setBusy(true);
    try {
      await api.delete(`/cards/${card.id}/vietqr`);
      toast.success(t("vietqr.toast.detached"));
      setConfig(null);
      if (onChange) onChange();
    } catch (err) {
      toast.error(t("vietqr.toast.detach_failed"));
    } finally {
      setBusy(false);
    }
  };

  // Mint a tokenized public setup link the sender can forward to the receiver
  // (email/SMS/chat). The receiver lands on /vietqr-setup/:token, fills in
  // their OWN bank details — sender never needs to know their account number.
  const handleMintLink = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/cards/${card.id}/vietqr/onboarding-link`, {});
      const url = r.data.url;
      // Try the modern clipboard API first; fall back to a hidden textarea.
      try {
        await navigator.clipboard.writeText(url);
        toast.success(t("vietqr.toast.link_copied", { h: r.data.expires_in_hours }), {
          description: url,
          duration: 12000,
        });
      } catch {
        // clipboard API blocked — show the URL in a long toast for manual copy
        toast.message(t("vietqr.toast.link_generated"), {
          description: url,
          duration: 20000,
        });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("vietqr.toast.link_failed"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  // ---- ENABLED STATE: show the live config card ----
  if (config) {
    return (
      <div
        className="mt-6 rounded-2xl border-2 border-emerald-600/30 bg-gradient-to-br from-emerald-50 to-[#FBF9F6] p-5"
        data-testid="vietqr-panel-enabled"
      >
        <div className="flex items-start gap-4">
          <span className="shrink-0 w-11 h-11 rounded-xl bg-emerald-600/15 text-emerald-700 flex items-center justify-center">
            <Globe size={22} weight="duotone" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="serif text-xl text-[#112318]">{t("vietqr.enabled.title")}</p>
              <span className="rounded-full bg-emerald-600 text-white text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-0.5">
                NAPAS
              </span>
            </div>
            <p className="text-sm text-[#4A5C50] mt-1 leading-relaxed">
              {t("vietqr.enabled.desc")}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-white border border-[#E5E0D8] p-3">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">{t("vietqr.enabled.bank_label")}</p>
                <p className="font-semibold text-[#112318] mt-0.5" data-testid="vietqr-bank-name">
                  {config.bank_name}
                </p>
                <p className="text-[11px] text-[#4A5C50] mono mt-0.5">BIN {config.bank_bin}</p>
              </div>
              <div className="rounded-lg bg-white border border-[#E5E0D8] p-3">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">{t("vietqr.enabled.account_label")}</p>
                <p className="font-semibold text-[#112318] mt-0.5 mono" data-testid="vietqr-account-last4">
                  •••• {config.account_last4}
                </p>
                <p className="text-[11px] text-[#4A5C50] mt-0.5 truncate">{config.account_name}</p>
              </div>
              {config.amount && (
                <div className="rounded-lg bg-white border border-[#E5E0D8] p-3">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">{t("vietqr.enabled.fixed_amount")}</p>
                  <p className="font-semibold text-[#112318] mt-0.5 mono">
                    {Number(config.amount).toLocaleString()} VND
                  </p>
                </div>
              )}
              {config.reference && (
                <div className="rounded-lg bg-white border border-[#E5E0D8] p-3">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">{t("vietqr.enabled.reference")}</p>
                  <p className="font-semibold text-[#112318] mt-0.5 mono text-xs">{config.reference}</p>
                </div>
              )}
            </div>

            {analytics && (
              <div className="mt-4 rounded-xl bg-white border border-emerald-200 p-4" data-testid="vietqr-analytics">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-emerald-700 font-semibold">{t("vietqr.analytics.live_activity")}</p>
                  <p className="text-[11px] text-[#4A5C50]">
                    {analytics.days_live === 0 ? t("vietqr.analytics.today") : t("vietqr.analytics.days_live", { n: analytics.days_live })}
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="serif text-2xl text-[#112318]" data-testid="analytics-views">{analytics.view_count}</p>
                    <p className="text-[10px] tracking-[0.18em] uppercase text-[#4A5C50] mt-0.5">{t("vietqr.analytics.phantom_views")}</p>
                  </div>
                  <div>
                    <p className="serif text-2xl text-emerald-700" data-testid="analytics-payments">{analytics.payment_count}</p>
                    <p className="text-[10px] tracking-[0.18em] uppercase text-[#4A5C50] mt-0.5">{t("vietqr.analytics.confirmed_pays")}</p>
                  </div>
                  <div>
                    <p className="serif text-2xl text-emerald-700" data-testid="analytics-vnd">
                      ₫{Number(analytics.total_confirmed_vnd || 0).toLocaleString()}
                    </p>
                    <p className="text-[10px] tracking-[0.18em] uppercase text-[#4A5C50] mt-0.5">{t("vietqr.analytics.total_vnd")}</p>
                  </div>
                </div>
                {analytics.recent_payments && analytics.recent_payments.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-emerald-100">
                    <p className="text-[10px] tracking-[0.18em] uppercase text-[#4A5C50] mb-1.5">{t("vietqr.analytics.recent_confirmed")}</p>
                    <ul className="space-y-1">
                      {analytics.recent_payments.slice(0, 3).map((p, i) => (
                        <li key={i} className="flex items-center justify-between text-xs">
                          <span className="text-[#4A5C50]">
                            {new Date(p.ts).toLocaleDateString()} {p.note ? `· ${p.note}` : ""}
                          </span>
                          <span className="mono font-semibold text-[#112318]">₫{Number(p.amount_vnd).toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-[11px] text-[#4A5C50] mt-3 italic leading-snug">
                  {t("vietqr.analytics.phantom_explainer")}
                </p>
              </div>
            )}
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={handleDetach}
                disabled={busy}
                className="rounded-lg bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 text-sm font-semibold px-4 py-2 disabled:opacity-50"
                data-testid="vietqr-detach-btn"
              >
                {busy ? t("vietqr.enabled.btn_detaching") : t("vietqr.enabled.btn_detach")}
              </button>
              <p className="text-[11px] text-[#4A5C50]">
                {t("vietqr.enabled.attached_at")} {config.attached_at ? new Date(config.attached_at).toLocaleString() : ""}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- DISABLED STATE: show enable / form ----
  return (
    <div
      className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5"
      data-testid="vietqr-panel-disabled"
    >
      <div className="flex items-start gap-4">
        <span className="shrink-0 w-11 h-11 rounded-xl bg-[#184A30]/10 text-[#184A30] flex items-center justify-center">
          <Bank size={22} weight="duotone" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="serif text-xl text-[#112318]">{t("vietqr.disabled.title")}</p>
            <span className="rounded-full bg-amber-100 text-amber-800 text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-0.5">
              {t("vietqr.disabled.badge")}
            </span>
          </div>
          <p className="text-sm text-[#4A5C50] mt-1 leading-relaxed">
            {t("vietqr.disabled.desc")}
          </p>

          {!showForm ? (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white text-sm font-semibold px-4 py-2.5"
                data-testid="vietqr-enable-btn"
              >
                <Bank size={16} weight="bold" /> {t("vietqr.disabled.btn_enter")}
              </button>
              <span className="text-[#4A5C50] text-xs">{t("vietqr.disabled.or")}</span>
              <button
                onClick={handleMintLink}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-xl bg-white border border-[#184A30] text-[#184A30] hover:bg-[#184A30]/5 text-sm font-semibold px-4 py-2.5 disabled:opacity-50"
                data-testid="vietqr-send-link-btn"
                title={t("vietqr.disabled.btn_send_link_tip")}
              >
                {busy ? <ArrowsClockwise size={16} className="animate-spin" /> : <LinkIcon size={16} weight="bold" />}
                {t("vietqr.disabled.btn_send_link")}
              </button>
            </div>
          ) : (
            <div className="mt-4 space-y-3" data-testid="vietqr-form">
              <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3 text-amber-900 text-xs flex items-start gap-2">
                <Warning size={18} weight="fill" className="shrink-0 mt-0.5 text-amber-600" />
                <div>
                  <p className="font-semibold mb-1">{t("vietqr.form.warn_title")}</p>
                  <p>
                    {t("vietqr.form.warn_body_a")}{" "}
                    <span className="font-semibold">{t("vietqr.form.warn_body_b")}</span>{" "}
                    {t("vietqr.form.warn_body_c")}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                  {t("vietqr.form.bank")}
                </label>
                <select
                  value={bankBin}
                  onChange={(e) => setBankBin(e.target.value)}
                  className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                  data-testid="vietqr-bank-select"
                >
                  {banks.map((b) => (
                    <option key={b.bin} value={b.bin}>
                      {b.name} (BIN {b.bin})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                  {t("vietqr.form.account_number")}
                </label>
                <input
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 24))}
                  placeholder="1900000000000"
                  className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm mono focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                  data-testid="vietqr-account-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                    {t("vietqr.form.account_name")}
                  </label>
                  <input
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value.toUpperCase().slice(0, 25))}
                    placeholder="NGUYEN VAN A"
                    className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                    data-testid="vietqr-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                    {t("vietqr.form.city")}
                  </label>
                  <input
                    value={city}
                    onChange={(e) => setCity(e.target.value.toUpperCase().slice(0, 15))}
                    placeholder="HA NOI"
                    className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                    data-testid="vietqr-city-input"
                  />
                </div>
              </div>

              <details className="text-xs">
                <summary className="cursor-pointer text-[#4A5C50] underline">
                  {t("vietqr.form.advanced")}
                </summary>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                      {t("vietqr.form.amount_vnd")}
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={t("vietqr.form.amount_ph")}
                      className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm mono"
                      data-testid="vietqr-amount-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                      {t("vietqr.form.reference")}
                    </label>
                    <input
                      value={reference}
                      onChange={(e) => setReference(e.target.value.slice(0, 25))}
                      placeholder="ORDER-123"
                      className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm mono"
                      data-testid="vietqr-reference-input"
                    />
                  </div>
                </div>
              </details>

              <label className="flex items-start gap-2 cursor-pointer text-sm text-[#112318] py-2" data-testid="vietqr-confirm-label">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1"
                  data-testid="vietqr-confirm-checkbox"
                />
                <span>
                  {t("vietqr.form.confirm")}
                </span>
              </label>

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={handleAttach}
                  disabled={busy || !confirmed || !accountNumber.trim() || !accountName.trim()}
                  className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] disabled:bg-[#184A30]/50 text-white text-sm font-semibold px-4 py-2.5 inline-flex items-center gap-2"
                  data-testid="vietqr-submit-btn"
                >
                  {busy ? <ArrowsClockwise size={16} className="animate-spin" /> : <CheckCircle size={16} weight="fill" />}
                  {busy ? t("vietqr.form.btn_attaching") : t("vietqr.form.btn_attach")}
                </button>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setConfirmed(false);
                  }}
                  className="text-sm text-[#4A5C50] hover:text-[#112318] inline-flex items-center gap-1"
                  data-testid="vietqr-cancel-btn"
                >
                  <X size={14} /> {t("vietqr.form.btn_cancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
